import type { EstadoPlan } from '@actual-app/core/server/rumbo/plan-app';
import type { TFunction } from 'i18next';

/**
 * Rumbo: las encuestas de Gavi (Vadik, 30-oct-2026; maqueta en
 * capturas-encuesta/). Las opciones (`valor`) son las que acepta el servidor
 * (sync-server/src/rumbo-encuesta.ts): si cambian aquí, cambiarlas allí.
 *   - `beta`: una vez, a los 10 días de uso y con 5 aperturas o más.
 *   - `dia40`: cinco días antes de que acabe la prueba de 45 días, solo a
 *     quien está probando (la tripulación de la beta no paga).
 */
export type NombreEncuesta = 'beta' | 'dia40';

type Opcion = { valor: string; texto: string };

export type Pregunta = {
  clave: string;
  titulo: string;
  ayuda?: string;
  /** Texto que se lee antes de contestar (el precio). */
  contexto?: string;
} & (
  | { tipo: 'una'; opciones: Opcion[] }
  | { tipo: 'varias'; opciones: Opcion[]; maximo?: number }
  | { tipo: 'escala'; desde: number; hasta: number; extremos: [string, string] }
  | { tipo: 'texto'; maximo: number; placeholder: string }
  | {
      // Una opción y, debajo, un texto libre (el «¿cuánto y por qué?»).
      tipo: 'una-y-texto';
      opciones: Opcion[];
      claveTexto: string;
      maximo: number;
      placeholder: string;
    }
) & {
    /** Solo se pregunta si otra respuesta lo pide. */
    siguiendo?: { clave: string; valores: string[] };
  };

export type Respuestas = Record<string, string | number | string[]>;

function funciones(t: TFunction): Opcion[] {
  return [
    { valor: 'apuntar', texto: t('Adding expenses') },
    { valor: 'importar', texto: t('Importing the statement') },
    { valor: 'asignar', texto: t('Assigning the month') },
    { valor: 'ahorros', texto: t('Savings goals') },
    { valor: 'pagos-fijos', texto: t('Fixed payments') },
    { valor: 'deudas', texto: t('Debts') },
    { valor: 'informes', texto: t('Reports') },
    { valor: 'tripulacion', texto: t('Crew (sharing with your partner)') },
    { valor: 'ayuda-gavi', texto: t('Help from Gavi') },
    { valor: 'diario', texto: t('The logbook') },
  ];
}

export function preguntasDe(
  t: TFunction,
  encuesta: NombreEncuesta,
): Pregunta[] {
  if (encuesta === 'dia40') {
    return [
      {
        clave: 'seguir',
        titulo: t('Will you stay with Rumbo when the trial ends?'),
        tipo: 'una',
        opciones: [
          { valor: 'mes', texto: t('Yes, monthly') },
          { valor: 'anio', texto: t('Yes, yearly') },
          { valor: 'no-se', texto: t("I don't know yet") },
          { valor: 'no', texto: t('No') },
        ],
      },
      {
        clave: 'frena',
        titulo: t('What is holding you back?'),
        ayuda: t('Choose as many as you like'),
        tipo: 'varias',
        siguiendo: { clave: 'seguir', valores: ['no-se', 'no'] },
        opciones: [
          { valor: 'precio', texto: t('The price') },
          { valor: 'poco-uso', texto: t("I don't use it enough") },
          { valor: 'falta', texto: t('Something is missing') },
          { valor: 'dificil', texto: t('I find it hard to use') },
          { valor: 'otra-app', texto: t('I prefer another app or Excel') },
          { valor: 'otra', texto: t('Something else') },
        ],
      },
      {
        clave: 'sirvio',
        titulo: t('What has helped you the most?'),
        tipo: 'texto',
        maximo: 300,
        placeholder: t('In a few words'),
      },
      {
        clave: 'quedarse',
        titulo: t('What would make you stay?'),
        tipo: 'texto',
        maximo: 300,
        placeholder: t('In a few words'),
      },
    ];
  }
  return [
    {
      clave: 'usa',
      titulo: t('What do you use most in Rumbo?'),
      ayuda: t('Choose up to 3'),
      tipo: 'varias',
      maximo: 3,
      opciones: funciones(t),
    },
    {
      clave: 'sobra',
      titulo: t('What is unnecessary or hard to understand?'),
      ayuda: t('Choose as many as you like'),
      tipo: 'varias',
      opciones: [
        ...funciones(t),
        { valor: 'nada', texto: t('Nothing, all good') },
      ],
    },
    {
      clave: 'facil',
      titulo: t('How easy is Rumbo to use?'),
      tipo: 'escala',
      desde: 1,
      hasta: 5,
      extremos: [t('I get lost'), t('Very easy')],
    },
    {
      clave: 'sinRumbo',
      titulo: t('If Rumbo disappeared tomorrow, how would you feel?'),
      tipo: 'una',
      opciones: [
        { valor: 'mucho', texto: t('It would really annoy me') },
        { valor: 'poco', texto: t('A little') },
        { valor: 'igual', texto: t("I wouldn't mind") },
      ],
    },
    {
      clave: 'antes',
      titulo: t('What did you use for your money before?'),
      tipo: 'una',
      opciones: [
        { valor: 'nada', texto: t('Nothing') },
        { valor: 'excel', texto: t('Excel or paper') },
        { valor: 'banco', texto: t("My bank's app") },
        { valor: 'app-gratis', texto: t('Fintonic or another free app') },
        { valor: 'app-pago', texto: t('A paid app (YNAB or another)') },
      ],
    },
    {
      clave: 'precio',
      titulo: t('If you had to pay for it, would you?'),
      contexto: t(
        "When the beta ends, Rumbo will cost 6.99 € a month or 49.99 € a year, with your partner included and 45 days to try it without a card. We won't charge you while you are in the beta.",
      ),
      tipo: 'una',
      opciones: [
        { valor: 'mes', texto: t('Yes, monthly') },
        { valor: 'anio', texto: t('Yes, yearly') },
        { valor: 'mas-barato', texto: t('Yes, but cheaper') },
        { valor: 'no', texto: t('No') },
        { valor: 'no-se', texto: t("I don't know yet") },
      ],
    },
    {
      clave: 'cuanto',
      titulo: t('How much would you pay a month, and why?'),
      tipo: 'una-y-texto',
      siguiendo: { clave: 'precio', valores: ['mas-barato', 'no'] },
      opciones: [
        { valor: '2,99', texto: '2,99 €' },
        { valor: '3,99', texto: '3,99 €' },
        { valor: '4,99', texto: '4,99 €' },
        { valor: '5,99', texto: '5,99 €' },
        { valor: 'nada', texto: t('Nothing') },
      ],
      claveTexto: 'porque',
      maximo: 300,
      placeholder: t('Why? (optional)'),
    },
    {
      clave: 'banco',
      titulo: t(
        'If Rumbo connected to your bank and transactions came in by themselves…',
      ),
      ayuda: t('Right now you upload the statement'),
      tipo: 'una',
      opciones: [
        { valor: 'pagaria', texto: t('That would make me pay') },
        { valor: 'igual', texto: t("I would pay the same, I don't mind") },
        {
          valor: 'no-quiero',
          texto: t("I don't want to connect my bank to any app"),
        },
      ],
    },
    {
      clave: 'anadir',
      titulo: t('What would you add or change?'),
      tipo: 'texto',
      maximo: 500,
      placeholder: t('Whatever comes to mind'),
    },
    {
      clave: 'contacto',
      titulo: t(
        'Can the person who runs Rumbo write to you for a 10-minute chat?',
      ),
      tipo: 'una',
      opciones: [
        { valor: 'si', texto: t('Yes') },
        { valor: 'no', texto: t('No') },
      ],
    },
  ];
}

/** Las preguntas que tocan con lo respondido hasta ahora. */
export function preguntasVisibles(
  preguntas: Pregunta[],
  respuestas: Respuestas,
): Pregunta[] {
  return preguntas.filter(
    p =>
      !p.siguiendo ||
      p.siguiendo.valores.includes(String(respuestas[p.siguiendo.clave])),
  );
}

/**
 * Solo lo que se envía: quita lo de las preguntas que ya no tocan (p. ej.
 * cambió el precio a «Sí, al año» tras decir cuánto pagaría) y lo vacío.
 */
export function respuestasParaEnviar(
  preguntas: Pregunta[],
  respuestas: Respuestas,
): Respuestas {
  const visibles = preguntasVisibles(preguntas, respuestas);
  const claves = new Set(
    visibles.flatMap(p =>
      p.tipo === 'una-y-texto' ? [p.clave, p.claveTexto] : [p.clave],
    ),
  );
  return Object.fromEntries(
    Object.entries(respuestas).filter(
      ([clave, v]) =>
        claves.has(clave) &&
        !(typeof v === 'string' && v.trim() === '') &&
        !(Array.isArray(v) && v.length === 0),
    ),
  );
}

/** Marca o desmarca una opción de varias, sin pasar del máximo. */
export function alternarOpcion(
  elegidas: string[],
  valor: string,
  maximo?: number,
): string[] {
  if (elegidas.includes(valor)) {
    return elegidas.filter(v => v !== valor);
  }
  if (maximo != null && elegidas.length >= maximo) {
    return elegidas;
  }
  return [...elegidas, valor];
}

const DIA_MS = 24 * 60 * 60 * 1000;
export const DIAS_USO_BETA = 10;
export const APERTURAS_BETA = 5;
export const APLAZAR_DIAS = 7;
/** La de antes de acabar la prueba sale los últimos 5 días (día 40 de 45). */
export const DIAS_ANTES_FIN = 5;

/** Lo que guarda cada móvil (ver `registroEncuesta.ts`). */
export type RegistroEncuesta = {
  /** Primera vez que se abrió Rumbo con la encuesta ya en la app (ms). */
  primeraVez: number;
  aperturas: number;
  /** «Ahora no»: no se vuelve a ofrecer hasta esta fecha (ms). */
  aplazada: Partial<Record<NombreEncuesta, number>>;
  /** Respondida o «Ahora no» por segunda vez: ya solo desde Ajustes. */
  cerrada: Partial<Record<NombreEncuesta, boolean>>;
};

/**
 * ¿Qué encuesta ofrecer al abrir la app? `null` si ninguna. La del día 40 va
 * primero: es la que caduca.
 */
export function encuestaQueToca({
  registro,
  plan,
  respondidas,
  ahora,
}: {
  registro: RegistroEncuesta;
  plan: EstadoPlan | null;
  /** Lo que dice el servidor (respondidas desde cualquier móvil). */
  respondidas: NombreEncuesta[];
  ahora: number;
}): NombreEncuesta | null {
  const libre = (e: NombreEncuesta) =>
    !registro.cerrada[e] &&
    !respondidas.includes(e) &&
    (registro.aplazada[e] ?? 0) <= ahora;

  const finPrueba = plan?.gratisHasta != null ? plan.gratisHasta * 1000 : null;
  if (
    plan?.estado === 'prueba' &&
    plan.tramo !== 'beta' &&
    finPrueba != null &&
    finPrueba > ahora &&
    finPrueba - ahora <= DIAS_ANTES_FIN * DIA_MS &&
    libre('dia40')
  ) {
    return 'dia40';
  }
  if (
    plan?.estado !== 'caducado' &&
    ahora - registro.primeraVez >= DIAS_USO_BETA * DIA_MS &&
    registro.aperturas >= APERTURAS_BETA &&
    libre('beta')
  ) {
    return 'beta';
  }
  return null;
}

/** «Ahora no»: la de la beta se aplaza una vez 7 días; luego, solo Ajustes. */
export function trasAhoraNo(
  registro: RegistroEncuesta,
  encuesta: NombreEncuesta,
  ahora: number,
): RegistroEncuesta {
  if (encuesta === 'beta' && registro.aplazada.beta == null) {
    return {
      ...registro,
      aplazada: { ...registro.aplazada, beta: ahora + APLAZAR_DIAS * DIA_MS },
    };
  }
  return { ...registro, cerrada: { ...registro.cerrada, [encuesta]: true } };
}
